import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/utils/api";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  ProductImageUploader,
  ProductImageAsset,
} from "../products/ProductImageUploader";

const DURATION_OPTIONS = [
  { value: "15 minutes", label: "15 minutes" },
  { value: "30 minutes", label: "30 minutes" },
  { value: "1 hour", label: "1 hour" },
  { value: "2 hours", label: "2 hours" },
  { value: "5 hours", label: "5 hours" },
  { value: "12 hours", label: "12 hours" },
  { value: "1 day", label: "1 day" },
];

const bookingSlotSchema = z.object({
  name: z.string().min(2, "Slot name must be at least 2 characters"),
  description: z.string().min(10, "Description must be at least 10 characters"),
  price: z.number().min(0, "Price must be greater than 0"),
  compareAtPrice: z.number().optional().nullable(),
  category: z.string().optional(),
  bookingType: z.enum(["car_hire", "apartment", "hotel", "service", "other"]),
  duration: z.string().min(1, "Duration is required"),
  timeInterval: z.number().min(15).max(1440).default(30), // 15 minutes to 24 hours
  capacity: z.number().min(1, "Capacity must be at least 1").default(1),
  status: z.enum(["draft", "active"]).default("draft"),
  tags: z.array(z.string()).default([]),
  availability: z.object({
    type: z.enum(["always", "scheduled", "custom"]).default("always"),
    schedule: z.array(z.object({
      day: z.string(),
      available: z.boolean().default(true),
      startTime: z.string().optional(),
      endTime: z.string().optional(),
    })).optional(),
  }).optional(),
});

type CreateBookingSlotFormData = z.infer<typeof bookingSlotSchema>;

// Calculate time interval based on duration
const calculateTimeInterval = (duration: string): number => {
  const lower = duration.toLowerCase();
  if (lower.includes('day')) {
    return 1440; // 1 day = 24 hours = 1440 minutes
  }
  if (lower.includes('hour')) {
    const hours = parseInt(lower.match(/\d+/)?.[0] || '1');
    if (hours >= 12) return 60; // For 12+ hours, use 1 hour intervals
    if (hours >= 5) return 30; // For 5+ hours, use 30 minute intervals
    if (hours >= 2) return 30; // For 2+ hours, use 30 minute intervals
    return 15; // For 1 hour, use 15 minute intervals
  }
  if (lower.includes('minute')) {
    const minutes = parseInt(lower.match(/\d+/)?.[0] || '30');
    if (minutes >= 30) return 15; // For 30+ minutes, use 15 minute intervals
    return 15; // For 15 minutes, use 15 minute intervals
  }
  return 30; // Default
};

export function CreateBookingSlotForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { storeId } = useCurrentStore();
  const [slotImages, setSlotImages] = useState<ProductImageAsset[]>([]);
  const [newTag, setNewTag] = useState("");

  const form = useForm<CreateBookingSlotFormData>({
    resolver: zodResolver(bookingSlotSchema),
    defaultValues: {
      name: "",
      description: "",
      price: 0,
      compareAtPrice: null,
      category: "",
      bookingType: "service",
      duration: "1 hour",
      timeInterval: 15, // Will be auto-calculated
      capacity: 1,
      status: "draft",
      tags: [],
    },
  });

  // Watch duration and auto-update timeInterval
  const duration = form.watch("duration");
  useEffect(() => {
    if (duration) {
      const calculatedInterval = calculateTimeInterval(duration);
      form.setValue("timeInterval", calculatedInterval, { shouldValidate: true });
    }
  }, [duration, form]);

  const mutation = useMutation({
    mutationFn: async (data: CreateBookingSlotFormData) => {
      const payload = {
        ...data,
        images: slotImages.map((image, index) => ({
          ...image,
          isDefault: index === 0 ? true : Boolean(image.isDefault),
        })),
      };
      return api.post("/api/booking-slots", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-slots"] });
      toast.success("Booking slot created successfully!");
      navigate("/dashboard/booking-slots");
    },
    onError: (error: unknown) => {
      console.error("Booking slot creation error:", error);
      toast.error("Failed to create booking slot");
    },
  });

  const addTag = () => {
    if (newTag.trim()) {
      const currentTags = form.getValues("tags");
      if (!currentTags.includes(newTag.trim())) {
        form.setValue("tags", [...currentTags, newTag.trim()]);
      }
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    const currentTags = form.getValues("tags");
    form.setValue(
      "tags",
      currentTags.filter((tag) => tag !== tagToRemove)
    );
  };

  const onSubmit = (data: CreateBookingSlotFormData) => {
    mutation.mutate(data);
  };

  const bookingType = form.watch("bookingType");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard/booking-slots")}
            className="h-10 text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-lg font-medium">Create New Booking Slot</h1>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Slot Name *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., Luxury Car Rental"
                          {...field}
                          className="h-10"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bookingType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Booking Type *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select booking type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="car_hire">Car Hire</SelectItem>
                          <SelectItem value="apartment">Apartment</SelectItem>
                          <SelectItem value="hotel">Hotel</SelectItem>
                          <SelectItem value="service">Service</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe what customers can book..."
                        {...field}
                        rows={4}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          className="h-10"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="compareAtPrice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Compare At Price</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                          className="h-10"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="capacity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Capacity *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          placeholder="1"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                          className="h-10"
                        />
                      </FormControl>
                      <FormDescription>
                        Number of people/guests/items
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select duration" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {DURATION_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        How long the booking lasts
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Hidden field for timeInterval - auto-calculated based on duration */}
                <FormField
                  control={form.control}
                  name="timeInterval"
                  render={({ field }) => (
                    <input type="hidden" {...field} />
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-10">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Luxury, Economy, Standard"
                        {...field}
                        className="h-10"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Availability & Schedule</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="availability.type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Availability Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="h-10">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="always">Always Available</SelectItem>
                        <SelectItem value="scheduled">Scheduled (Days & Times)</SelectItem>
                        <SelectItem value="custom">Custom Dates</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Set when this booking slot is available for customers
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch("availability.type") === "scheduled" && (
                <div className="space-y-4">
                  <FormLabel>Weekly Schedule</FormLabel>
                  {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => {
                    const schedule = form.watch("availability.schedule") || [];
                    const daySchedule = schedule.find((s: any) => s.day === day) || { day, available: false, startTime: "", endTime: "" };
                    const dayIndex = schedule.findIndex((s: any) => s.day === day);

                    return (
                      <div key={day} className="flex items-center gap-4 p-3 border rounded-lg">
                        <div className="flex items-center gap-2 w-32">
                          <input
                            type="checkbox"
                            checked={daySchedule.available}
                            onChange={(e) => {
                              const currentSchedule = form.getValues("availability.schedule") || [];
                              if (e.target.checked) {
                                if (dayIndex === -1) {
                                  form.setValue("availability.schedule", [
                                    ...currentSchedule,
                                    { day, available: true, startTime: "09:00", endTime: "17:00" },
                                  ]);
                                } else {
                                  const updated = [...currentSchedule];
                                  updated[dayIndex] = { ...updated[dayIndex], available: true };
                                  form.setValue("availability.schedule", updated);
                                }
                              } else {
                                if (dayIndex !== -1) {
                                  const updated = [...currentSchedule];
                                  updated[dayIndex] = { ...updated[dayIndex], available: false };
                                  form.setValue("availability.schedule", updated);
                                }
                              }
                            }}
                            className="h-4 w-4"
                          />
                          <Label className="font-medium">{day}</Label>
                        </div>
                        {daySchedule.available && (
                          <div className="flex items-center gap-2 flex-1">
                            <Input
                              type="time"
                              value={daySchedule.startTime || "09:00"}
                              onChange={(e) => {
                                const currentSchedule = form.getValues("availability.schedule") || [];
                                const updated = [...currentSchedule];
                                if (dayIndex === -1) {
                                  updated.push({ day, available: true, startTime: e.target.value, endTime: daySchedule.endTime || "17:00" });
                                } else {
                                  updated[dayIndex] = { ...updated[dayIndex], startTime: e.target.value };
                                }
                                form.setValue("availability.schedule", updated);
                              }}
                              className="h-9"
                            />
                            <span className="text-muted-foreground">to</span>
                            <Input
                              type="time"
                              value={daySchedule.endTime || "17:00"}
                              onChange={(e) => {
                                const currentSchedule = form.getValues("availability.schedule") || [];
                                const updated = [...currentSchedule];
                                if (dayIndex === -1) {
                                  updated.push({ day, available: true, startTime: daySchedule.startTime || "09:00", endTime: e.target.value });
                                } else {
                                  updated[dayIndex] = { ...updated[dayIndex], endTime: e.target.value };
                                }
                                form.setValue("availability.schedule", updated);
                              }}
                              className="h-9"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Images</CardTitle>
            </CardHeader>
            <CardContent>
              <ProductImageUploader
                images={slotImages}
                onChange={setSlotImages}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tags</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Add a tag"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  className="h-10"
                />
                <Button type="button" onClick={addTag} className="h-10">
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.watch("tags").map((tag) => (
                  <div
                    key={tag}
                    className="flex items-center gap-2 bg-muted px-3 py-1 rounded-md"
                  >
                    <span className="text-sm">{tag}</span>
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/dashboard/booking-slots")}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Creating..." : "Create Booking Slot"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

